import { default as EsriGraphic } from '@arcgis/core/Graphic';
import type ArcGisCollection from '@arcgis/core/core/Collection';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { createGraphicsLayer } from '@rsos/maps/providers/esri/graphics/layers';
import {
  createSketchWidget,
  SKETCH_WIDGET_TITLE,
} from '@rsos/maps/providers/esri/widgets/createSketchWidget';
import { PsapState } from '@rsos/reduxDataStore';
import { SinatraState } from '@rsos/sinatra';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkEsriSketchWidgetAccessSelector from '@rsos/utils/checkEsriSketchWidgetAccessSelector';
import usePortalType from '@rsos/utils/usePortalType';
import { trackToggleMarkupMode } from '../../../../../../../../apps/emergency_data/src/components/JurisdictionView/Map/gainsight/gisTrackings';
import {
  saveAllGraphics,
  mapMarkupSelector,
} from '../../../../../../../../apps/emergency_data/src/components/JurisdictionView/Map/mapMarkupSlice';
import type { MapInstance as CompassMapInstance } from './CompassButton/CompassButton.types';
import ExportMapButton from './ExportMapButton';
import type { MapInstance as ExportMapInstance } from './ExportMapButton/ExportMapButton.types';
import { GISFeedbackButton } from './GISFeedbackButton';
import { MeasureLineTool } from './MeasureLineTool';
import NotePopover from './NotePopover';
import { SketchPanelExtension } from './SketchPanelExtension';
import WidgetContainer from './WidgetContainer';
import type {
  EsriSketchGLProps,
  NotePopoverState,
  LayerConfig,
  GraphicsLayer,
  SketchWidget,
  SketchEvent,
  GraphicsClickEvent,
  GraphicsClickResponse,
  Graphic,
} from './types';
import { useSketchHelpers } from './useSketchHelpers';
import { getColorValue, GEOMETRY_TYPES } from './utils';

type HitTestItem = __esri.HitTestItem;

const SKETCH_GRAPHICS_LAYER_TITLE = 'Sketch Graphics Layer';
const sketchEvents = [
  'create',
  'update',
  'delete',
  'move',
  'reshape',
  'transform',
];

// Type for createGraphicsLayer parameter
type CreateGraphicsLayerParams = Parameters<typeof createGraphicsLayer>[0];

interface RootState {
  psaps: PsapState;
  sinatra: SinatraState;
}

const createPolygonSymbol = (
  boundaryColor = '',
  polygonColor = '',
): SimpleFillSymbol => {
  const boundaryRGB = getColorValue(boundaryColor);
  const polygonRGB = getColorValue(polygonColor);

  return new SimpleFillSymbol({
    color: [...polygonRGB, 0.2], // Add transparency
    outline: new SimpleLineSymbol({
      color: boundaryRGB,
      width: 2,
    }),
  });
};

const EsriSketchGL: React.FC<EsriSketchGLProps> = React.memo(
  ({ map, mapProvider }) => {
    const { isPremiumUser } = usePortalType();
    const dispatch = useDispatch();

    // Redux state for map markup persistence
    const reduxMapMarkup = useSelector(mapMarkupSelector);
    const [isSketchVisible, setIsSketchVisible] = useState<boolean>(false);
    const [hasRestoredGraphics, setHasRestoredGraphics] =
      useState<boolean>(false);
    const [isMeasurementVisible, setIsMeasurementVisible] =
      useState<boolean>(false);
    const [sketchWidget, setSketchWidget] = useState<SketchWidget | null>(null);
    const [graphicsLayer, setGraphicsLayer] = useState<GraphicsLayer | null>(
      null,
    );
    const [notePopover, setNotePopover] = useState<NotePopoverState | null>(
      null,
    );

    const hasEsriMapAccess = useSelector((state: RootState) =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    );

    const hasSketchWidgetAccess =
      useSelector((state: RootState) =>
        checkEsriSketchWidgetAccessSelector({
          psaps: state.psaps,
          sinatra: state.sinatra,
        }),
      ) &&
      hasEsriMapAccess &&
      isPremiumUser &&
      mapProvider === ESRI;

    // Define before passing into useSketchHelpers
    const updateGraphicLayer = useCallback((): void => {
      if (graphicsLayer) {
        const serializedGraphics: unknown[] = [];
        graphicsLayer.graphics.forEach((g: Graphic) => {
          serializedGraphics.push((g as any).toJSON());
        });
        dispatch(saveAllGraphics(serializedGraphics));
      }
    }, [graphicsLayer, dispatch]);

    const {
      handleClosePopover,
      handleSketchComplete,
      handleGraphicsClick,
      updateTextSymbols,
      handleNotePopoverSubmit,
      updateGraphicNoteLive,
    } = useSketchHelpers({
      graphicsLayer,
      notePopover,
      setNotePopover,
      updateGraphicLayer,
    });

    // Set up sketch widget event listeners
    useEffect(() => {
      if (!(sketchWidget && handleSketchComplete)) return;

      const handles = sketchEvents.map(name =>
        (sketchWidget as any).on(name, (event: unknown) => {
          handleSketchComplete(event as SketchEvent);
        }),
      );
      return () =>
        handles.forEach(h => (h as { remove?: () => void })?.remove?.());
    }, [sketchWidget, handleSketchComplete]);

    // Set up graphics layer click handler
    useEffect(() => {
      if (graphicsLayer && map?.mapInstance?.view) {
        const view = map.mapInstance.view;
        const clickHandler = view.on('click', event => {
          const typedEvent = event as GraphicsClickEvent;
          const opts = {
            // TODO: Fix the type of graphicsLayer. I'm guessing on the type here. The type we define for GraphicsLayer isn't right.
            include: graphicsLayer as unknown as ArcGisCollection<HitTestItem>,
          };
          view.hitTest(event, opts).then((response: unknown) => {
            const typedResponse = response as GraphicsClickResponse;
            const polygonResult = typedResponse?.results?.find(r => {
              const g = r?.graphic as Graphic | undefined;
              return (
                !!g &&
                g.geometry?.type === GEOMETRY_TYPES.POLYGON &&
                !g.attributes?.isTextLabel &&
                !g.attributes?.isDistanceLabel
              );
            });

            if (polygonResult?.graphic) {
              handleGraphicsClick({
                graphic: polygonResult.graphic,
                mapPoint: typedEvent.mapPoint,
              });
            } else {
              // Clicked outside polygons (or on labels) - close popover
              handleClosePopover();
            }
          });
        });

        // Add view change listener to update text labels on zoom
        const viewChangeHandler = view.watch('zoom', () => {
          updateTextSymbols();
        });

        return () => {
          if (clickHandler) {
            (clickHandler as { remove: () => void }).remove();
          }
          if (viewChangeHandler) {
            (viewChangeHandler as { remove: () => void }).remove();
          }
        };
      }
    }, [
      graphicsLayer,
      map,
      handleGraphicsClick,
      updateTextSymbols,
      handleClosePopover,
      sketchWidget,
    ]);

    // Create and toggle the visibility of the sketch widget
    useEffect(() => {
      if (
        hasSketchWidgetAccess &&
        map?.mapInstance?.map?.layers &&
        map?.mapInstance?.view
      ) {
        const mapLayers = map.mapInstance.map.layers;
        const mapView = map.mapInstance.view;
        // Note: find is documented as returning `HTMLElement | Widget | null |
        //   undefined` per
        //   https://developers.arcgis.com/javascript/latest/api-reference/esri-views-ui-UI.html#find
        const existingWidget = mapView.ui.find(
          SKETCH_WIDGET_TITLE,
        ) as unknown as SketchWidget | null | undefined;

        if (existingWidget) {
          existingWidget.visible = isSketchVisible;
          setSketchWidget(existingWidget);
          // Ensure we have a reference to the existing graphics layer so clicks work
          try {
            const existingLayer =
              (Array.isArray(mapLayers)
                ? (mapLayers as any[]).find(
                    (l: any) =>
                      l?.title === SKETCH_GRAPHICS_LAYER_TITLE ||
                      l?.id === SKETCH_GRAPHICS_LAYER_TITLE,
                  )
                : (mapLayers as any).find &&
                  (mapLayers as any).find((l: any) => {
                    return (
                      l?.title === SKETCH_GRAPHICS_LAYER_TITLE ||
                      l?.id === SKETCH_GRAPHICS_LAYER_TITLE
                    );
                  })) || null;

            if (existingLayer) {
              setGraphicsLayer(existingLayer as unknown as GraphicsLayer);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to find existing graphics layer:', e);
          }
        } else {
          const layerConfigs: LayerConfig = {
            title: SKETCH_GRAPHICS_LAYER_TITLE,
            id: SKETCH_GRAPHICS_LAYER_TITLE,
          };

          try {
            (async (): Promise<void> => {
              const newGraphicsLayer = await createGraphicsLayer({
                configs: layerConfigs,
              } as unknown as CreateGraphicsLayerParams);

              mapLayers.push(newGraphicsLayer);
              setGraphicsLayer(newGraphicsLayer as unknown as GraphicsLayer);

              const newSketchWidget = createSketchWidget({
                graphicsLayer: newGraphicsLayer,
                mapView,
              });

              mapView.ui.add(newSketchWidget, 'top-leading');
              newSketchWidget.when(() => {
                newSketchWidget.visible = false;
                setSketchWidget(newSketchWidget as SketchWidget);
              });
            })();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              'Error creating graphics layer or sketch widget:',
              error,
            );
          }
        }
      }
    }, [hasSketchWidgetAccess, map, isSketchVisible]);

    // Restore graphics from Redux state when layer is ready (only once)
    useEffect(() => {
      if (!graphicsLayer || hasRestoredGraphics) {
        return;
      }

      // If there's no saved data, mark as restored to prevent future restore attempts
      if (!reduxMapMarkup?.graphicsJSON?.length) {
        setHasRestoredGraphics(true);
        return;
      }

      // Only restore if the layer is empty (prevents duplicating on first draw)
      const graphicsCount =
        (graphicsLayer.graphics as unknown as { length: number })?.length ?? 0;
      if (graphicsCount > 0) {
        setHasRestoredGraphics(true);
        return;
      }

      try {
        const graphics = reduxMapMarkup.graphicsJSON.map((json: unknown) =>
          EsriGraphic.fromJSON(json),
        );
        graphics.forEach((graphic: EsriGraphic) => graphicsLayer.add(graphic));
        setHasRestoredGraphics(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to restore graphics:', error);
        setHasRestoredGraphics(true); // Prevent infinite retries
      }
    }, [graphicsLayer, hasRestoredGraphics, reduxMapMarkup?.graphicsJSON]);

    // Update polygon symbol in real-time when colors change
    useEffect(() => {
      if (notePopover?.graphic && graphicsLayer) {
        // Find the polygon by OBJECTID
        const polygonGraphic = graphicsLayer.graphics.find(
          (g: Graphic) =>
            g.attributes.OBJECTID ===
            (notePopover.graphic.attributes as { OBJECTID: string | number })
              .OBJECTID,
        );

        if (polygonGraphic) {
          // Update the polygon symbol directly
          polygonGraphic.symbol = createPolygonSymbol(
            notePopover.boundaryColor,
            notePopover.polygonColor,
          );

          // Update the display
          updateTextSymbols();
        }
      }
    }, [
      notePopover?.boundaryColor,
      notePopover?.polygonColor,
      notePopover?.graphic,
      graphicsLayer,
      updateTextSymbols,
    ]);

    // Live updates when notePopover changes
    useEffect(() => {
      if (notePopover?.graphic && graphicsLayer) {
        updateGraphicNoteLive(
          notePopover.note,
          notePopover.boundaryColor,
          notePopover.polygonColor,
        );
      }
    }, [notePopover, graphicsLayer, updateGraphicNoteLive]);

    const handleToggleSketch = useCallback((): void => {
      const newVisibility = !isSketchVisible;
      setIsSketchVisible(newVisibility);

      // Track markup tool toggle
      trackToggleMarkupMode({
        isEnabled: newVisibility,
      });
    }, [isSketchVisible]);

    const handleToggleMeasurement = useCallback((): void => {
      setIsMeasurementVisible(prev => !prev);
    }, []);

    const handleSubmit = (
      note: string,
      boundaryColor: string,
      polygonColor: string,
    ): void => {
      handleNotePopoverSubmit(note, boundaryColor, polygonColor);

      // Save all graphics to Redux after user clicks "Save"
      if (graphicsLayer) {
        const serializedGraphics: unknown[] = [];
        graphicsLayer.graphics.forEach((g: Graphic) => {
          serializedGraphics.push((g as any).toJSON());
        });
        dispatch(saveAllGraphics(serializedGraphics));
      }

      if (sketchWidget) {
        sketchWidget.cancel(); // set tool to pointer
      }
    };

    const handleClose = (): void => {
      handleClosePopover();
      if (sketchWidget) {
        sketchWidget.cancel(); // set tool to pointer
      }
    };

    const handleNoteChange = (field: string, value: string): void => {
      if (notePopover) {
        setNotePopover({
          ...notePopover,
          [field]: value,
        });
      }
    };

    if (!hasSketchWidgetAccess) {
      return null;
    }

    return (
      <>
        <WidgetContainer
          onMeasurementClick={handleToggleMeasurement}
          onSketchClick={handleToggleSketch}
          isMeasurementActive={isMeasurementVisible}
          isSketchActive={isSketchVisible}
          mapInstance={map?.mapInstance as unknown as CompassMapInstance}
        />

        {map?.mapInstance?.view && (
          <MeasureLineTool
            isSketchVisible={isSketchVisible}
            mapView={map.mapInstance.view}
          />
        )}

        <ExportMapButton
          mapInstance={map?.mapInstance as unknown as ExportMapInstance}
          onExportStart={() => {
            // do nothing right now
          }}
          onExportComplete={() => {
            // do nothing right now
          }}
          isVisible={isSketchVisible}
        />

        <SketchPanelExtension isSketchVisible={isSketchVisible}>
          <GISFeedbackButton />
        </SketchPanelExtension>

        {notePopover && (
          <NotePopover
            key={notePopover?.graphic?.attributes?.OBJECTID?.toString() || ''}
            onClose={handleClose}
            onSubmit={handleSubmit}
            note={notePopover.note}
            boundaryColor={notePopover.boundaryColor}
            polygonColor={notePopover.polygonColor}
            onNoteChange={handleNoteChange}
          />
        )}
      </>
    );
  },
);

EsriSketchGL.displayName = 'EsriSketchGL';

export default EsriSketchGL;
